/**
 * Connect's own settings in the one settings list (I-2510): the keys the
 * shell owns — „Darstellung" (`theme`, `locale`, `appearance`),
 * „Benachrichtigungen" (`shell` → `notifications.*`: whether they are on and
 * how long they are kept; what each module and notification type does per
 * device type joins at runtime from master's type register,
 * `notificationSettings.ts`), „Formulare" (`form`, the kit's form axes),
 * „Tabellen" (`table`, what every NxTable starts with), „Editor" (`editor`,
 * the tools of every rich-text editor), „Tabs und Kacheln" (`shell` → `tiles.*`,
 * the tab bar's workspaces), „Startseite" (`shell` → `home.unread.*`, the counts
 * on the app tiles), „Suche" (`search`, the universal search),
 * „Adressen für die Apps" (`endpoints`: the LAN entries of the installation's
 * signed endpoint list, which master assembles and serves at
 * `GET /v1/endpoints`, backend/master/endpoints), „Anmeldeseite" (`login`:
 * the branding of the login page, which master serves before sign-in at
 * `GET /v1/login/branding`) and „Fernwartung" (`support`: the standing access
 * of remote support). The apps declare theirs in their own modules
 * (`ConnectAppModule.settingsSections`); the list renders both alike.
 *
 * One core key has no section here: `spell_check` (I-3006), the spell check's
 * dictionary shared by every app. Its only field, `personal_dictionary`, is a
 * map of words — data, no scalar row — so it is the hub topic „Wörterbuch“
 * (`general/personalDictionary`, `PersonalDictionarySettings.vue`), read and
 * extended by the apps through `useSpellDictionary()` (`@blpde/connect/config`,
 * `config/spellCheck.ts`); an app's own switch for its spell check is a row of
 * the app's section and links to that page.
 *
 * Options and defaults come from where the values are used: the kit's design
 * axes, NxTable's settings, the search's defaults, the default appearance.
 * Master keeps the same defaults on the platform layer of these core keys.
 * Texts: `connect.settingsList.*` in Connect's own message table.
 */
import { designAxes } from '@blpde/nx-ui';
import type { ConnectSettingsField, ConnectSettingsSection } from '@blpde/connect';
import { APPEARANCE_CONFIG_KEY, DEFAULT_APPEARANCE_ID, FORM_CONFIG_KEY, TABLE_CONFIG_KEY } from '@/appearance/look';
import { defaultSearchSettings } from '@/search/settings';
import { HOME_UNREAD_DEFAULTS } from '@/home/unreadCounts';
import { SUPPORT_MINUTE_STEPS } from '@/support/durations';

const T = 'connect.settingsList';

/** The route of the look designer (UI „Design“, its hub topic `general/design`). */
const LOOK_DESIGNER_PATH = '/settings/general/design';
/** The route of the search's sources and order (its hub topic `system/search`). */
const SEARCH_SOURCES_PATH = '/settings/system/search';

function texts(name: string): Pick<ConnectSettingsField, 'labelKey' | 'descriptionKey'> {
  return { labelKey: `${T}.fields.${name}.label`, descriptionKey: `${T}.fields.${name}.description` };
}

function options(name: string, values: readonly string[]) {
  return values.map((value) => ({ value, labelKey: `${T}.fields.${name}.options.${value}` }));
}

/** A choice over a design axis of the kit the key `form` holds: its values, its default. */
function axisField(
  axis: 'input' | 'reveal' | 'inputicon' | 'savestatus' | 'caps' | 'groups' | 'ink',
  name: string
): ConnectSettingsField {
  return {
    key: FORM_CONFIG_KEY,
    path: axis,
    control: 'choice',
    options: options(name, designAxes[axis].values),
    default: designAxes[axis].default,
    ...texts(name),
  };
}

function tableSwitch(path: string, name: string, fallback: boolean): ConnectSettingsField {
  return { key: TABLE_CONFIG_KEY, path, control: 'switch', default: fallback, ...texts(name) };
}

const searchDefaults = defaultSearchSettings();

/** The key the kit's rich-text editors read their tools from (`useNxSettings().get('editor')`). */
const EDITOR_CONFIG_KEY = 'editor';

/** The colours a comment can have: the stored hex value and the caption's name. */
const COMMENT_COLORS = [
  { value: '#3177b9', name: 'blue' },
  { value: '#c0392b', name: 'red' },
  { value: '#2e7d32', name: 'green' },
  { value: '#d97706', name: 'orange' },
  { value: '#7c3aed', name: 'purple' },
] as const;

/**
 * The key of the installation's endpoint list (backend/master/accountconfig/
 * endpoints.go). Its document is `lan_entries`, a map of entries by id; the
 * rows below edit the entry `lan`, further entries are set through the config
 * path (admin door) until the list has an editor for several. Master refuses
 * the key on every layer but the installation's (the default tenant's), so
 * the rows are `installationOnly`.
 */
const ENDPOINTS_CONFIG_KEY = 'endpoints';

/** A text of the LAN entry `lan` (an origin or a host:port); empty means none. */
function lanText(path: 'https' | 'imap' | 'smtp', name: string, keywords: readonly string[]): ConnectSettingsField {
  return {
    key: ENDPOINTS_CONFIG_KEY,
    path: `lan_entries.lan.${path}`,
    control: 'text',
    maxLength: 255,
    default: null,
    adminOnly: true,
    installationOnly: true,
    keywords,
    ...texts(name),
  };
}

/**
 * The key of the login page's branding (backend/master/accountconfig/login.go,
 * edge rebuild plan W19): every field is an administrator's, set on a tenant's
 * or a company's layer, and master serves the effective document before
 * anybody signed in. The rows below are its texts, links and switch; the
 * logos, the background, the hostnames and the preview are the page editor's
 * (`LoginPageSettings.vue`, the section's link).
 */
export const LOGIN_CONFIG_KEY = 'login';

/** The route of the login page editor (its hub topic `system/loginPage`). */
const LOGIN_PAGE_EDITOR_PATH = '/settings/system/loginPage';

/** The longest texts master accepts (`loginTextLimits`). */
const LOGIN_TEXT_LIMITS = { title: 200, subtitle: 500, footer: 1000 } as const;

/** A text of the login page in one language. */
function loginText(
  field: keyof typeof LOGIN_TEXT_LIMITS,
  locale: 'de' | 'en',
  name: string,
  keywords: readonly string[]
): ConnectSettingsField {
  return {
    key: LOGIN_CONFIG_KEY,
    path: `${field}.${locale}`,
    control: 'text',
    maxLength: LOGIN_TEXT_LIMITS[field],
    default: null,
    adminOnly: true,
    keywords,
    ...texts(name),
  };
}

/** A link of the login page; master accepts https addresses only. */
function loginLink(name: 'imprint' | 'privacy' | 'help', keywords: readonly string[]): ConnectSettingsField {
  return {
    key: LOGIN_CONFIG_KEY,
    path: `links.${name}`,
    control: 'text',
    maxLength: 2048,
    default: null,
    adminOnly: true,
    keywords,
    ...texts(`loginLink${name[0]!.toUpperCase()}${name.slice(1)}`),
  };
}

/**
 * The key of remote support (Fernwartung, backend/master/accountconfig/
 * support.go, `support`): the installation's standing access, `standing`. With
 * it BLP support may connect without an admin approving the session — only
 * with a request the control plane verified with a passkey. An installation
 * setting like `endpoints`: admin-only fields on the installation's layer (the
 * default tenant's). `standing.enabled` switches it (default off),
 * `standing.account` says whether the session works as one fixed account
 * (`standing.account_id`, picked on the page) or as a superadmin made for each
 * session (`ephemeral`, the default), `standing.max_minutes` caps a session
 * (15 to 480, default 60). The key is guarded (`Definition.Guarded`): master
 * refuses a fixed account with more rights than the writer's own
 * (`more_rights`), an inactive one (`invalid_account`), temporary superadmins
 * for a writer who is none (`not_superadmin`), and every write without the
 * edge link (503 `write_unavailable`). The rows below are the switch, the
 * choice and the cap; the account picker and the recent access are the page's
 * (`SupportSettings.vue`, the section's link).
 */
export const SUPPORT_CONFIG_KEY = 'support';

/** The route of the remote support page (its hub topic `system/support`). */
const SUPPORT_PAGE_PATH = '/settings/system/support';

/** What a standing session may last at most when none is set (minutes; master's `DefaultStandingMinutes`). */
export const SUPPORT_DEFAULT_MAX_MINUTES = 60;

/**
 * The key of the shell's own settings: its field `tiles` is the workspaces', its
 * field `notifications` is what master's notification service reads. Both
 * sections below list fields of this one key.
 */
export const SHELL_CONFIG_KEY = 'shell';
/** The id of the section „Tabs und Kacheln"; the section keeps the key's name. */
export const SHELL_SECTION_ID = 'shell';
/** The section of the home page; its account rows join at runtime (`homeUnreadSettings.ts`). */
export const HOME_SECTION_ID = 'home';
/** The id of the section „Benachrichtigungen": their own settings and the module groups of the type register. */
export const NOTIFICATIONS_SECTION_ID = 'notifications';

/** The sections of Connect's own keys, in the order the list shows them. */
export const connectSettingsSections: ConnectSettingsSection[] = [
  {
    id: 'appearance',
    labelKey: `${T}.sections.appearance.title`,
    descriptionKey: `${T}.sections.appearance.description`,
    keywords: ['Theme', 'Hell', 'Dunkel', 'Dark mode', 'Sprache', 'Language', 'Erscheinungsbild', 'Look'],
    fields: [
      {
        key: 'theme',
        path: 'mode',
        control: 'choice',
        options: options('theme', ['light', 'dark']),
        default: 'light',
        keywords: ['Dark mode', 'Nachtmodus', 'Theme'],
        ...texts('theme'),
      },
      {
        key: 'locale',
        path: 'locale',
        control: 'choice',
        options: options('locale', ['de', 'en']),
        default: 'de',
        keywords: ['Language', 'Deutsch', 'English'],
        ...texts('locale'),
      },
      {
        key: APPEARANCE_CONFIG_KEY,
        path: 'active',
        control: 'appearance',
        default: DEFAULT_APPEARANCE_ID,
        keywords: ['Farben', 'Schrift', 'Look', 'Marke', 'Brand'],
        link: { labelKey: `${T}.fields.appearance.link`, to: LOOK_DESIGNER_PATH },
        ...texts('appearance'),
      },
      {
        key: APPEARANCE_CONFIG_KEY,
        path: 'allow_personal',
        control: 'switch',
        default: true,
        adminOnly: true,
        ...texts('allowPersonal'),
      },
    ],
  },
  {
    id: NOTIFICATIONS_SECTION_ID,
    labelKey: `${T}.sections.notifications.title`,
    descriptionKey: `${T}.sections.notifications.description`,
    keywords: [
      'Benachrichtigungen',
      'Notifications',
      'Glocke',
      'Mitteilungen',
      'Hinweise',
      'Systemhinweis',
      'System notification',
    ],
    // What master's notification service acts on for the account
    // (backend/master/accountconfig/notifications.go, I-2915). The settings per
    // module and per type for each device type are groups after these, built
    // from the type register at runtime (`notificationSettings.ts`).
    fields: [
      {
        key: SHELL_CONFIG_KEY,
        path: 'notifications.enabled',
        control: 'switch',
        default: true,
        keywords: ['Benachrichtigungen', 'Notifications', 'Glocke', 'stumm', 'aus'],
        ...texts('notificationsEnabled'),
      },
      {
        key: SHELL_CONFIG_KEY,
        path: 'notifications.history_days',
        control: 'number',
        min: 1,
        max: 90,
        step: 1,
        unitKey: `${T}.fields.notificationsHistoryDays.unit`,
        default: 30,
        keywords: ['Verlauf', 'History', 'Aufbewahrung', 'löschen', 'Tage'],
        ...texts('notificationsHistoryDays'),
      },
    ],
  },
  {
    id: 'form',
    labelKey: `${T}.sections.form.title`,
    descriptionKey: `${T}.sections.form.description`,
    keywords: ['Eingabefelder', 'Felder', 'Inputs'],
    fields: [
      axisField('input', 'input'),
      axisField('reveal', 'reveal'),
      axisField('inputicon', 'inputIcon'),
      axisField('savestatus', 'saveStatus'),
      // The look of a form around its fields (I-2510): small headings, field
      // groups, the colour of the values.
      { ...axisField('caps', 'caps'), keywords: ['Versalien', 'Großbuchstaben', 'Labels', 'Überschriften'] },
      { ...axisField('groups', 'groups'), keywords: ['Gruppen', 'Karten', 'Feldgruppen', 'Linie'] },
      { ...axisField('ink', 'ink'), keywords: ['Textfarbe', 'Werte', 'Markenfarbe', 'Schriftfarbe'] },
    ],
  },
  {
    id: 'table',
    labelKey: `${T}.sections.table.title`,
    descriptionKey: `${T}.sections.table.description`,
    keywords: ['Listen', 'Grid', 'Tabelle'],
    // A table's own settings over these (`table.tables.<settings key>`, set in
    // its gear with „Diese Tabelle"): „Überall übernehmen" removes them.
    objectOverrides: {
      key: TABLE_CONFIG_KEY,
      field: 'tables',
      countLabelKey: `${T}.sections.table.ownTables`,
    },
    fields: [
      {
        key: TABLE_CONFIG_KEY,
        path: 'zebra',
        control: 'switch',
        values: { on: 'on', off: 'off' },
        default: 'on',
        keywords: ['Streifen', 'Zeilen'],
        ...texts('zebra'),
      },
      tableSwitch('horizontal_lines', 'horizontalLines', true),
      tableSwitch('vertical_lines', 'verticalLines', false),
      {
        key: TABLE_CONFIG_KEY,
        path: 'row_open.single',
        control: 'choice',
        options: options('rowOpen', ['none', 'here', 'tab', 'split', 'workspace']),
        default: 'none',
        keywords: ['Klick', 'öffnen', 'Datensatz', 'Tab', 'Splitscreen', 'click', 'open'],
        ...texts('rowOpenSingle'),
      },
      {
        key: TABLE_CONFIG_KEY,
        path: 'row_open.double',
        control: 'choice',
        options: options('rowOpen', ['none', 'here', 'tab', 'split', 'workspace']),
        default: 'here',
        keywords: ['Doppelklick', 'öffnen', 'Datensatz', 'Tab', 'Splitscreen', 'double click', 'open'],
        ...texts('rowOpenDouble'),
      },
      {
        key: TABLE_CONFIG_KEY,
        path: 'row_open.middle',
        control: 'choice',
        options: options('rowOpen', ['none', 'here', 'tab', 'split', 'workspace']),
        default: 'tab',
        keywords: ['Mittelklick', 'mittlere Maustaste', 'Tab', 'Splitscreen', 'middle click'],
        ...texts('rowOpenMiddle'),
      },
      {
        key: TABLE_CONFIG_KEY,
        path: 'row_density',
        control: 'choice',
        options: [
          { value: null, labelKey: `${T}.fields.rowDensity.options.perTable` },
          ...options('rowDensity', ['airy', 'normal', 'compact']),
        ],
        default: null,
        keywords: ['Zeilenhöhe', 'Dichte', 'kompakt'],
        ...texts('rowDensity'),
      },
      {
        key: TABLE_CONFIG_KEY,
        path: 'header',
        control: 'choice',
        options: options('tableHeader', ['subtle', 'brand']),
        default: 'subtle',
        keywords: ['Kopfzeile', 'Spaltenköpfe', 'Markenfarbe'],
        ...texts('tableHeader'),
      },
      {
        key: TABLE_CONFIG_KEY,
        path: 'cell_align',
        control: 'choice',
        options: options('cellAlign', ['middle', 'top']),
        default: 'middle',
        keywords: ['Ausrichtung', 'oben', 'mehrzeilig'],
        ...texts('cellAlign'),
      },
      tableSwitch('search_row', 'searchRow', true),
      { ...tableSwitch('always_show_scrollbars', 'scrollbars', true), advanced: true },
    ],
  },
  {
    id: 'editor',
    labelKey: `${T}.sections.editor.title`,
    descriptionKey: `${T}.sections.editor.description`,
    keywords: ['Texteditor', 'Text editor', 'Kommentar', 'Anmerkung', 'Comment'],
    fields: [
      {
        key: EDITOR_CONFIG_KEY,
        path: 'comment_enabled',
        control: 'switch',
        default: true,
        keywords: ['Kommentar', 'Anmerkung', 'Notiz', 'F6', 'Comment'],
        ...texts('commentEnabled'),
      },
      {
        key: EDITOR_CONFIG_KEY,
        path: 'comment_text',
        control: 'text',
        maxLength: 500,
        default: '{{user.name}} ({{date}}): ',
        keywords: ['Kommentar', 'Platzhalter', 'Name', 'Datum', 'Vorlage'],
        ...texts('commentText'),
      },
      {
        key: EDITOR_CONFIG_KEY,
        path: 'comment_color',
        control: 'choice',
        options: COMMENT_COLORS.map(({ value, name }) => ({
          value,
          labelKey: `${T}.fields.commentColor.options.${name}`,
        })),
        default: COMMENT_COLORS[0].value,
        keywords: ['Kommentar', 'Farbe', 'Hervorhebung', 'Colour'],
        ...texts('commentColor'),
      },
    ],
  },
  {
    id: HOME_SECTION_ID,
    labelKey: `${T}.sections.home.title`,
    descriptionKey: `${T}.sections.home.description`,
    keywords: ['Startseite', 'Home', 'Apps', 'Ungelesen', 'Unread', 'Zahl', 'Badge', 'Count', 'Chat', 'Mail'],
    fields: [
      {
        key: SHELL_CONFIG_KEY,
        path: 'home.unread.enabled',
        control: 'switch',
        default: HOME_UNREAD_DEFAULTS.enabled,
        ...texts('homeUnreadEnabled'),
      },
      {
        key: SHELL_CONFIG_KEY,
        path: 'home.unread.chat',
        control: 'switch',
        default: HOME_UNREAD_DEFAULTS.chat,
        ...texts('homeUnreadChat'),
      },
      {
        key: SHELL_CONFIG_KEY,
        path: 'home.unread.mail',
        control: 'switch',
        default: HOME_UNREAD_DEFAULTS.mail,
        ...texts('homeUnreadMail'),
      },
      {
        key: SHELL_CONFIG_KEY,
        path: 'home.unread.display',
        control: 'choice',
        options: options('homeUnreadDisplay', ['number', 'dot']),
        default: HOME_UNREAD_DEFAULTS.display,
        ...texts('homeUnreadDisplay'),
      },
      {
        key: SHELL_CONFIG_KEY,
        path: 'home.unread.limit',
        control: 'number',
        min: 9,
        max: 9999,
        step: 1,
        default: HOME_UNREAD_DEFAULTS.limit,
        ...texts('homeUnreadLimit'),
      },
      {
        key: SHELL_CONFIG_KEY,
        path: 'home.unread.chat_muted',
        control: 'switch',
        default: HOME_UNREAD_DEFAULTS.chat_muted,
        ...texts('homeUnreadChatMuted'),
      },
      {
        key: SHELL_CONFIG_KEY,
        path: 'home.unread.chat_direct',
        control: 'switch',
        default: HOME_UNREAD_DEFAULTS.chat_direct,
        ...texts('homeUnreadChatDirect'),
      },
      {
        key: SHELL_CONFIG_KEY,
        path: 'home.unread.chat_channels',
        control: 'switch',
        default: HOME_UNREAD_DEFAULTS.chat_channels,
        ...texts('homeUnreadChatChannels'),
      },
      {
        key: SHELL_CONFIG_KEY,
        path: 'home.unread.mail_folders',
        control: 'choice',
        options: options('homeUnreadMailFolders', ['inbox', 'all']),
        default: HOME_UNREAD_DEFAULTS.mail_folders,
        ...texts('homeUnreadMailFolders'),
      },
      {
        key: SHELL_CONFIG_KEY,
        path: 'home.unread.mail_junk',
        control: 'switch',
        default: HOME_UNREAD_DEFAULTS.mail_junk,
        advanced: true,
        ...texts('homeUnreadMailJunk'),
      },
      {
        key: SHELL_CONFIG_KEY,
        path: 'home.unread.mail_trash',
        control: 'switch',
        default: HOME_UNREAD_DEFAULTS.mail_trash,
        advanced: true,
        ...texts('homeUnreadMailTrash'),
      },
      {
        key: SHELL_CONFIG_KEY,
        path: 'home.unread.refresh_seconds',
        control: 'number',
        min: 15,
        max: 3600,
        step: 5,
        unit: 's',
        default: HOME_UNREAD_DEFAULTS.refresh_seconds,
        advanced: true,
        ...texts('homeUnreadRefresh'),
      },
    ],
  },
  {
    id: SHELL_SECTION_ID,
    labelKey: `${T}.sections.shell.title`,
    descriptionKey: `${T}.sections.shell.description`,
    keywords: ['Tabs', 'Tab bar', 'Taskleiste', 'Taskbar', 'Kacheln', 'Arbeitsflächen', 'Tiles', 'Workspaces', 'Browser-Titel', 'Browser title', 'Tab-Titel', 'Matchcode', 'Belegnummer'],
    fields: [
      {
        key: SHELL_CONFIG_KEY,
        path: 'tiles.enabled',
        control: 'switch',
        default: true,
        ...texts('tilesEnabled'),
      },
      {
        key: SHELL_CONFIG_KEY,
        path: 'tiles.restore',
        control: 'switch',
        default: true,
        ...texts('tilesRestore'),
      },
      {
        key: SHELL_CONFIG_KEY,
        path: 'tiles.navbar',
        control: 'choice',
        options: options('tilesNavbar', ['shared', 'tile']),
        default: 'shared',
        ...texts('tilesNavbar'),
      },
      {
        key: SHELL_CONFIG_KEY,
        path: 'tiles.split_target',
        control: 'choice',
        options: options('tilesSplitTarget', ['reuse', 'new']),
        default: 'reuse',
        ...texts('tilesSplitTarget'),
      },
      {
        key: SHELL_CONFIG_KEY,
        path: 'browser_title.record',
        control: 'choice',
        options: options('browserTitleRecord', ['number', 'name', 'both']),
        default: 'number',
        ...texts('browserTitleRecord'),
      },
      {
        key: SHELL_CONFIG_KEY,
        path: 'browser_title.area',
        control: 'switch',
        default: true,
        ...texts('browserTitleArea'),
      },
      {
        key: SHELL_CONFIG_KEY,
        path: 'browser_title.product',
        control: 'switch',
        default: true,
        ...texts('browserTitleProduct'),
      },
    ],
  },
  {
    id: 'search',
    labelKey: `${T}.sections.search.title`,
    descriptionKey: `${T}.sections.search.description`,
    keywords: ['Befehlspalette', 'Suche', '⌘K', 'Treffer'],
    link: { labelKey: `${T}.sections.search.link`, to: SEARCH_SOURCES_PATH },
    fields: [
      {
        key: 'search',
        path: 'limit',
        control: 'number',
        min: 1,
        max: 20,
        step: 1,
        default: searchDefaults.limit,
        ...texts('searchLimit'),
      },
      {
        key: 'search',
        path: 'topHits',
        control: 'switch',
        default: searchDefaults.topHits,
        ...texts('searchTopHits'),
      },
      {
        key: 'search',
        path: 'askAssistant',
        control: 'switch',
        default: searchDefaults.askAssistant,
        keywords: ['KI', 'Assistent', 'AI'],
        ...texts('searchAskAssistant'),
      },
      {
        key: 'search',
        path: 'learnCorrections',
        control: 'switch',
        default: searchDefaults.learnCorrections,
        keywords: ['Korrektur', 'lernen', 'Meinten Sie'],
        ...texts('searchLearnCorrections'),
      },
      {
        key: 'search',
        path: 'chipInteraction',
        control: 'choice',
        options: options('searchChipInteraction', ['direct', 'menu']),
        default: searchDefaults.chipInteraction,
        ...texts('searchChipInteraction'),
      },
      {
        key: 'search',
        path: 'chipWording',
        control: 'choice',
        options: options('searchChipWording', ['logic', 'plain']),
        default: searchDefaults.chipWording,
        ...texts('searchChipWording'),
      },
    ],
  },
  {
    id: 'endpoints',
    labelKey: `${T}.sections.endpoints.title`,
    descriptionKey: `${T}.sections.endpoints.description`,
    keywords: ['Endpunktliste', 'Adressliste', 'Endpoint list', 'LAN', 'lokales Netz', 'Server-Adresse', 'Desktop-App'],
    fields: [
      lanText('https', 'lanHttps', ['LAN', 'Adresse', 'URL', 'Server', 'lokales Netz', 'intern']),
      lanText('imap', 'lanImap', ['IMAP', 'Postfach', 'Mail', 'Port', '993']),
      lanText('smtp', 'lanSmtp', ['SMTP', 'Versand', 'Mail', 'Port', '465']),
      {
        key: ENDPOINTS_CONFIG_KEY,
        path: 'lan_entries.lan.priority',
        control: 'number',
        min: 0,
        max: 65535,
        step: 1,
        default: 10,
        adminOnly: true,
        installationOnly: true,
        advanced: true,
        keywords: ['Priorität', 'Reihenfolge', 'zuerst', 'Rangfolge'],
        ...texts('lanPriority'),
      },
    ],
  },
  {
    id: 'login',
    labelKey: `${T}.sections.login.title`,
    descriptionKey: `${T}.sections.login.description`,
    keywords: ['Anmeldung', 'Login', 'Branding', 'Logo', 'Firmenlogo', 'Impressum', 'Datenschutz', 'Sign-in page', 'Imprint', 'Privacy'],
    link: { labelKey: `${T}.sections.login.link`, to: LOGIN_PAGE_EDITOR_PATH },
    fields: [
      loginText('title', 'de', 'loginTitleDe', ['Überschrift', 'Titel', 'Willkommen', 'Deutsch']),
      loginText('title', 'en', 'loginTitleEn', ['Heading', 'Title', 'Welcome', 'Englisch']),
      loginText('subtitle', 'de', 'loginSubtitleDe', ['Untertitel', 'Hinweis', 'Deutsch']),
      loginText('subtitle', 'en', 'loginSubtitleEn', ['Subtitle', 'Hint', 'Englisch']),
      loginText('footer', 'de', 'loginFooterDe', ['Fußzeile', 'Fusszeile', 'Deutsch']),
      loginText('footer', 'en', 'loginFooterEn', ['Footer', 'Englisch']),
      loginLink('imprint', ['Impressum', 'Imprint', 'Legal notice']),
      loginLink('privacy', ['Datenschutz', 'Datenschutzerklärung', 'Privacy', 'GDPR']),
      loginLink('help', ['Hilfe', 'Support', 'Help']),
      {
        key: LOGIN_CONFIG_KEY,
        path: 'show_powered_by',
        control: 'switch',
        default: true,
        adminOnly: true,
        keywords: ['Powered by', 'BLP', 'Formica', 'Hinweis', 'Herkunft'],
        ...texts('loginShowPoweredBy'),
      },
    ],
  },
  {
    id: 'support',
    labelKey: `${T}.sections.support.title`,
    descriptionKey: `${T}.sections.support.description`,
    keywords: ['Fernwartung', 'Support', 'BLP', 'Aufschalten', 'Wartung', 'Dauerfernwartung', 'Remote support', 'Standing access'],
    link: { labelKey: `${T}.sections.support.link`, to: SUPPORT_PAGE_PATH },
    fields: [
      {
        key: SUPPORT_CONFIG_KEY,
        path: 'standing.enabled',
        control: 'switch',
        default: false,
        adminOnly: true,
        installationOnly: true,
        // The installation layer of `support` is edited on the public door (no lock store there).
        lockable: false,
        keywords: ['Dauerfernwartung', 'ohne Rückfrage', 'Passkey', 'Standing access'],
        ...texts('supportStandingEnabled'),
      },
      {
        key: SUPPORT_CONFIG_KEY,
        path: 'standing.account',
        control: 'choice',
        options: options('supportStandingAccount', ['fixed', 'ephemeral']),
        default: 'ephemeral',
        adminOnly: true,
        installationOnly: true,
        // The installation layer of `support` is edited on the public door (no lock store there).
        lockable: false,
        keywords: ['Superadmin', 'Konto', 'Support-Konto', 'temporär', 'pro Sitzung'],
        ...texts('supportStandingAccount'),
      },
      {
        key: SUPPORT_CONFIG_KEY,
        path: 'standing.max_minutes',
        control: 'select',
        options: SUPPORT_MINUTE_STEPS.map((minutes) => ({
          value: minutes,
          labelKey: `${T}.fields.supportStandingMaxMinutes.options.${minutes}`,
        })),
        default: SUPPORT_DEFAULT_MAX_MINUTES,
        adminOnly: true,
        installationOnly: true,
        // The installation layer of `support` is edited on the public door (no lock store there).
        lockable: false,
        keywords: ['Dauer', 'Sitzung', 'Minuten', 'Stunden', 'Zeitlimit'],
        ...texts('supportStandingMaxMinutes'),
      },
    ],
  },
];
